import { bundleDataLookup } from '../../../services/componentAPI';
import type { OrderItem, BundleData, PackingLogEntry } from '../../../types/apiTypes';
import {
  parseParentDisplayNameFromBundleTitle,
  parseParentSkuFromBundleTitle,
} from '@/shared/utils/parseBundleTitle';
import { stripByKink } from '@/shared/utils/stripByKink';
import { normalizeSku } from '@/shared/utils/normalizeSku';

export const maxComponentQuantity: number = 6;

const UNITS_IN_NAME = /\((\d+)\s*units?\)\s*$/i;

export function unitsCountFromName(name: string | undefined): number | null {
  if (!name) {
    return null;
  }
  const match = name.match(UNITS_IN_NAME);
  if (!match) {
    return null;
  }
  return Number.parseInt(match[1], 10);
}

/** Pick-row unit contribution: expanded chips are 1; adjusted-qty chips use quantity or the `(N units)` name suffix. */
export function pickLineUnitCount(item: Pick<OrderItem, 'isAdjustedQty' | 'quantity' | 'name'>): number {
  if (item.isAdjustedQty !== true) {
    return 1;
  }
  if (typeof item.quantity === 'number' && Number.isFinite(item.quantity) && item.quantity > 0) {
    return item.quantity;
  }
  return unitsCountFromName(item.name) ?? 1;
}

interface ShipstationItemOption {
  name?: string;
  value?: string;
}

export interface RawOrderItem {
  sku?: string;
  name?: string;
  quantity?: number;
  options?: ShipstationItemOption[];
  [key: string]: unknown;
}

const createStableId = (sku: string, index: string | number, parentId?: string | null): string => {
  const base = parentId ? `${parentId}-${sku}-${index}` : `${sku}-${index}`;
  return base;
};

export const getBundleDataOptionsProperty = (options: ShipstationItemOption[], propertyName: string): string | null => {
  const target = options.find(opt => opt.name?.trim() === propertyName);
  if (!target || typeof target.value !== 'string') return null;
  return target.value;
};

export function collectBundleParentSkusFromItems(
  items: readonly Pick<RawOrderItem, 'options'>[],
): Set<string> {
  const parentSkus = new Set<string>();
  for (const item of items) {
    const options = item.options;
    if (!options?.length) {
      continue;
    }
    const title = getBundleDataOptionsProperty(options, 'sb bundle title');
    const parentSku = parseParentSkuFromBundleTitle(title);
    if (parentSku) {
      parentSkus.add(normalizeSku(parentSku));
    }
  }
  return parentSkus;
}

export const extractBundleData = (
  item: RawOrderItem,
  getBundleDataOptionsPropertyFn: (options: ShipstationItemOption[], propertyName: string) => string | null
): { variantId: string; groupId: string; quantity: string } | null => {
  if (!item.options) {
    return null;
  }
  const options = item.options;
  if (!Array.isArray(options)) return null;

  const variantIdQuantityStr = getBundleDataOptionsPropertyFn(options, 'sb bundle variant id qty');
  if (!variantIdQuantityStr) {
    return null;
  }
  const [variantId, quantity] = variantIdQuantityStr.split(':');
  const groupStr = getBundleDataOptionsPropertyFn(options, 'sb bundle group');
  if (!groupStr) {
    return null;
  }
  const groupId = groupStr.split(' ')[0];
  return { variantId, groupId, quantity };
};

interface BundleGroup {
  quantity: string;
  sku: string;
  name: string | null | undefined;
  components: RawOrderItem[];
}

function mergeBundleComponentsBySku(components: RawOrderItem[]): RawOrderItem[] {
  const bySku = new Map<string, RawOrderItem>();
  for (const component of components) {
    const key = (component.sku ?? '').trim().toUpperCase();
    if (!key) continue;
    const existing = bySku.get(key);
    if (!existing) {
      bySku.set(key, { ...component, quantity: component.quantity ?? 1 });
    } else {
      existing.quantity = (existing.quantity ?? 1) + (component.quantity ?? 1);
    }
  }
  return [...bySku.values()];
}


function bundleDataFromTitle(title: string | null, variantId: string): BundleData | null {
  const parentSku = parseParentSkuFromBundleTitle(title);
  if (!parentSku?.startsWith('RSV-')) return null;
  const rawName = parseParentDisplayNameFromBundleTitle(title);
  const productName = rawName ? stripByKink(rawName) || rawName : parentSku;
  return {
    productSKU: parentSku,
    productName,
    productVariantId: variantId,
  };
}

async function resolveBundleData(
  variantId: string,
  item: RawOrderItem,
  bundleDataCache: Record<string, BundleData>,
  newCacheEntries: Record<string, BundleData>,
): Promise<BundleData | null> {
  if (newCacheEntries[variantId]) return newCacheEntries[variantId];
  if (bundleDataCache[variantId]) return bundleDataCache[variantId];

  let bundleData: BundleData | null = null;
  try {
    bundleData = await bundleDataLookup(variantId);
  } catch (error: unknown) {
    console.error('Error fetching bundle data:', error);
  }

  if (bundleData) {
    newCacheEntries[variantId] = bundleData;
    return bundleData;
  }

  const options = Array.isArray(item.options) ? item.options : [];
  const title = getBundleDataOptionsProperty(options, 'sb bundle title');
  const fallback = bundleDataFromTitle(title, variantId);
  if (fallback) {
    newCacheEntries[variantId] = fallback;
    return fallback;
  }

  return null;
}

export const prepareItemListCore = async (
  items: RawOrderItem[],
  bundleDataCache: Record<string, BundleData>,
  setBundleDataCache: React.Dispatch<React.SetStateAction<Record<string, BundleData>>>,
  extractBundleDataFn: (item: RawOrderItem) => { variantId: string; groupId: string; quantity: string } | null,
  maxComponentQuantity: number
): Promise<OrderItem[]> => {
  const filteredItems = items.filter((item) =>
    normalizeSku(item.sku).startsWith('RSV-')
  );

  const itemsForUI: OrderItem[] = [];
  const standaloneItems: RawOrderItem[] = [];
  const bundles: Record<string, BundleGroup> = {};

  const newCacheEntries: Record<string, BundleData> = {};
  let cacheWasUpdated = false;

  for (const item of filteredItems) {
    const itemVariantData = extractBundleDataFn(item);

    if (!itemVariantData) {
      standaloneItems.push(item);
    } else {
      const { groupId, variantId } = itemVariantData;

      if (!bundles[groupId]) {
        const wasCached = Boolean(newCacheEntries[variantId] || bundleDataCache[variantId]);
        const bundleData = await resolveBundleData(
          variantId,
          item,
          bundleDataCache,
          newCacheEntries,
        );

        if (!bundleData) {
          console.warn(
            `Bundle group ${groupId}: no parent resolved for variant ${variantId}; promoting line to standalone`,
          );
          standaloneItems.push(item);
          continue;
        }

        if (!wasCached && newCacheEntries[variantId]) {
          cacheWasUpdated = true;
        }

        bundles[groupId] = {
          quantity: itemVariantData.quantity,
          sku: bundleData.productSKU,
          name: stripByKink(bundleData.productName) || bundleData.productName,
          components: [item]
        };
      } else {
        bundles[groupId].components.push(item);
      }
    }
  }

  if (cacheWasUpdated) {
    setBundleDataCache(prev => ({
      ...prev,
      ...newCacheEntries
    }));
  }

  const reconstructedParentSkus = collectBundleParentSkusFromItems(filteredItems);
  for (const bundle of Object.values(bundles)) {
    reconstructedParentSkus.add(normalizeSku(bundle.sku));
  }

  standaloneItems.forEach(item => {
    const sku = item.sku ?? '';
    if (reconstructedParentSkus.has(normalizeSku(sku))) {
      return;
    }
    const name = item.name ?? '';
    const qty = item.quantity ?? 1;
    const displayName = stripByKink(name) || name;

    if (qty > maxComponentQuantity) {
      itemsForUI.push({
        id: createStableId(sku, 0),
        scanned: false,
        name: `${displayName} (${qty} units)`,
        sku,
        quantity: qty,
        options: item.options,
        isAdjustedQty: true,
      });
      return;
    }

    const itemsArray = Array.from({ length: qty }, (_, index): OrderItem => ({
      id: createStableId(sku, index),
      scanned: false,
      name: displayName,
      sku,
      quantity: qty,
      options: item.options,
    }));
    itemsForUI.push(...itemsArray);
  });

  Object.entries(bundles).forEach(([groupId, bundle]) => {
    const bundleQuantity = Number(bundle.quantity);
    const mergedComponents = mergeBundleComponentsBySku(bundle.components);

    for (let i = 0; i < bundleQuantity; i++) {
      // Include groupId in stable IDs so multiple bundle groups with the same
      // SKU (e.g. 2× RSV-X-B-HOOK.2.COLLAR with different compositions)
      // produce distinct parent and component IDs.
      const parentBundleId = createStableId(bundle.sku, `${groupId}-${i}`);
      const parentBundle: OrderItem = {
        id: parentBundleId,
        scanned: false,
        name: bundle.name || bundle.sku,
        sku: bundle.sku,
        isBundleParent: true,
      };
      itemsForUI.push(parentBundle);

      mergedComponents.forEach((component: RawOrderItem, componentIndex: number) => {
        const compQty = component.quantity ?? 1;
        const compSku = component.sku ?? '';
        const compName = component.name ?? '';
        const componentQuantityPerBundle = Math.ceil(compQty / bundleQuantity);
        const quantityToCopy = componentQuantityPerBundle > maxComponentQuantity ? 1 : componentQuantityPerBundle;
        let componentName: string;
        let componentSku = compSku;
        let isAdjustedQty = false;
        let adjustedQuantity: number | undefined;
        if (componentQuantityPerBundle > maxComponentQuantity){
          componentName = stripByKink(compName) + ` (${componentQuantityPerBundle} units)`;
          if (componentName === "1ft Long Link Stainless Steel Light Chain (12 units)"){
            componentName = "3ft Long Link Stainless Steel Light Chain (4 units)";
            componentSku = "RSV-C-SS-B-LONG.LINK.CHAIN-4MM-3FT";
          }
          isAdjustedQty = true;
          adjustedQuantity = unitsCountFromName(componentName) ?? componentQuantityPerBundle;
        }
        else{
          componentName = stripByKink(compName) || compName;
        }
        const componentsArray = Array.from({ length: quantityToCopy }, (_, index): OrderItem => ({
          id: createStableId(componentSku, `${i}-${componentIndex}-${index}`, parentBundleId),
          scanned: false,
          name: componentName,
          sku: componentSku,
          isComponent: true,
          isAdjustedQty,
          quantity: adjustedQuantity,
          parentId: parentBundleId
        }));
        itemsForUI.push(...componentsArray);
      });
    }
  });

  return itemsForUI;
};

export const handleSubmit = async (
  orderItems: OrderItem[],
  shipToName: string,
  orderNumberCin7: string,
  submitPackingLog: (data: PackingLogEntry[]) => Promise<string | undefined>,
  setMessage: (msg: string) => void,
  setWebcamOn?: (on: boolean) => void
): Promise<string | undefined> => {
  const dataToSubmit: PackingLogEntry[] = orderItems
    .filter((item) => !item.isComponent)
    .map((item) => ({
      customer: shipToName,
      ordernumber: orderNumberCin7,
      SKU: item.sku,
    }));

  const result = await submitPackingLog(dataToSubmit);
  setMessage(result ?? '');
  return result;
};

export const handleReset = (
  orderItems: OrderItem[],
  setOrderItems: (items: OrderItem[]) => void,
  setMessage: (msg: string) => void,
  setIsReadyForSubmit: (ready: boolean) => void
): void => {
  const resetItems = orderItems.map(item => ({
    ...item,
    scanned: false
  }));
  setOrderItems(resetItems);
  setMessage('Order reset successfully');
  setIsReadyForSubmit(false);
};
